import { useMemo, useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import FormControlLabel from '@mui/material/FormControlLabel'
import Link from '@mui/material/Link'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import { problemMessage } from '../../api/baseApi'
import {
  useCreateTenantMutation,
  useLazyGetOperatorOpenApiyamlQuery,
  useListOperatorTenantsQuery,
  useListPlansQuery,
  type TenantStatus,
} from '../../api/operator'
import { download } from '../../ui/download'
import { formatDate, formatMoney, parseMoney, todayISO } from '../../model/money'
import { operator } from '../../model/routes'
import { EmptyState, Loading, QueryError } from '../../ui/QueryState'
import { Balance, centsText, MoneyField, OperatorHeader, StatusChip } from './common'
import { shown } from '../../api/useIntent'

type Filter = TenantStatus | 'all'

/** Which register dialog is open: a new tenant, or an id being adopted. */
type Registering = { adopting?: string } | null

/**
 * Every customer, and where each account stands today.
 *
 * Active tenants by default, because that is who is being billed; an
 * offboarded tenant stays listed under its own filter, since its ledger
 * outlives its content and an unpaid balance can still be collected.
 */
export function OperatorTenants() {
  const tenants = useListOperatorTenantsQuery()
  const [filter, setFilter] = useState<Filter>('active')
  const [registering, setRegistering] = useState<Registering>(null)

  const shown = useMemo(
    () => (tenants.data?.tenants ?? []).filter((tenant) => filter === 'all' || tenant.status === filter),
    [tenants.data, filter],
  )

  if (tenants.isLoading) return <Loading label="Loading tenants…" />
  if (tenants.error) return <QueryError error={tenants.error} title="Could not load tenants" />

  const unregistered = tenants.data?.unregistered ?? []

  return (
    <Stack spacing={3}>
      <OperatorHeader
        title="Tenants"
        subtitle="Every customer on the platform, what they are on, and what they owe."
        actions={
          <>
            <ContractDownload />
            <Button variant="contained" onClick={() => setRegistering({})}>
              New tenant
            </Button>
          </>
        }
      />

      {unregistered.length > 0 && (
        <Alert severity="info">
          {unregistered.length === 1 ? 'One tenant has' : `${unregistered.length} tenants have`} administrators but no
          registration, so {unregistered.length === 1 ? 'it is' : 'they are'} not listed: <strong>{unregistered.join(', ')}</strong>.
          Adopting registers {unregistered.length === 1 ? 'it' : 'one'} under the same id, keeping everything it holds.
          <Stack direction="row" spacing={1} useFlexGap sx={{ mt: 1, flexWrap: 'wrap' }}>
            {unregistered.map((id) => (
              <Button key={id} size="small" variant="outlined" color="inherit" onClick={() => setRegistering({ adopting: id })}>
                Adopt {id}
              </Button>
            ))}
          </Stack>
        </Alert>
      )}

      <ToggleButtonGroup
        size="small"
        exclusive
        value={filter}
        onChange={(_event, next: Filter | null) => next && setFilter(next)}
        sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5 } }}
      >
        <ToggleButton value="active">Active</ToggleButton>
        <ToggleButton value="offboarding">Offboarding</ToggleButton>
        <ToggleButton value="offboarded">Offboarded</ToggleButton>
        <ToggleButton value="all">All</ToggleButton>
      </ToggleButtonGroup>

      {shown.length === 0 ? (
        <EmptyState title={filter === 'active' ? 'No active tenants' : 'Nothing here'}>
          {filter === 'active' && <Typography color="text.secondary">Register a customer to start billing them.</Typography>}
        </EmptyState>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tenant</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell align="right">Balance</TableCell>
                <TableCell align="right">Overdue</TableCell>
                <TableCell>Next charge</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((tenant) => (
                <TableRow key={tenant.id} hover>
                  <TableCell>
                    <Link component={RouterLink} to={operator.tenant(tenant.id)} underline="hover" sx={{ fontWeight: 600 }}>
                      {tenant.name}
                    </Link>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {tenant.id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <StatusChip status={tenant.status} />
                  </TableCell>
                  <TableCell>
                    {!tenant.billed ? (
                      <Typography variant="body2" color="text.secondary">
                        Not billed
                      </Typography>
                    ) : tenant.account.planName ? (
                      <>
                        {tenant.account.planName}
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {formatMoney(tenant.account.amount ?? 0)} {tenant.account.interval}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.disabled">
                        No agreement
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Balance cents={tenant.account.balance} />
                  </TableCell>
                  <TableCell align="right" sx={{ color: tenant.account.overdue > 0 ? 'error.main' : 'text.disabled' }}>
                    {tenant.account.overdue > 0 ? formatMoney(tenant.account.overdue) : '—'}
                  </TableCell>
                  <TableCell>{formatDate(tenant.account.nextChargeOn)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Mounted only while open, so each starts afresh: an adoption with
          its id, a new tenant with none. */}
      {registering && <CreateTenantDialog adopting={registering.adopting} onClose={() => setRegistering(null)} />}
    </Stack>
  )
}

/**
 * Registering a customer: who they are, what they pay, and who
 * administers their content. Only the name and the id are required; an
 * agreement and an administrator can be added afterwards from the
 * tenant's own screens.
 *
 * Adopting is the same registration over an id that already holds
 * content, so the id is fixed. A tenant that is not billed has no
 * agreement to state, so its plan and billing contact are not asked for.
 */
function CreateTenantDialog({ adopting, onClose }: { adopting?: string; onClose: () => void }) {
  const navigate = useNavigate()
  const plans = useListPlansQuery()
  const [create, creation] = useCreateTenantMutation()

  const [id, setId] = useState(adopting ?? '')
  const [billed, setBilled] = useState(true)
  const [name, setName] = useState('')
  const [contactName, setContactName] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [administratorEmail, setAdministratorEmail] = useState('')
  const [planId, setPlanId] = useState('')
  const [interval, setInterval] = useState<'monthly' | 'annual'>('monthly')
  const [amount, setAmount] = useState('')
  const [starts, setStarts] = useState(todayISO())
  const [terms, setTerms] = useState('20')

  const offered = (plans.data?.plans ?? []).filter((plan) => plan.active)
  const plan = offered.find((candidate) => candidate.id === planId)
  const choosePlan = (next: string, nextInterval = interval) => {
    setPlanId(next)
    const chosen = offered.find((candidate) => candidate.id === next)
    setAmount(chosen ? centsText(nextInterval === 'annual' ? chosen.annual : chosen.monthly) : '')
  }

  const cents = parseMoney(amount)
  const ready = id.trim() !== '' && name.trim() !== '' && (!billed || !plan || (cents !== null && starts !== ''))

  const submit = async () => {
    const created = await create({
      createTenant: {
        id: id.trim(),
        name: name.trim(),
        contactName: (billed && contactName.trim()) || undefined,
        contactEmail: (billed && contactEmail.trim()) || undefined,
        administratorEmail: administratorEmail.trim() || undefined,
        billed,
        agreement:
          billed && plan
            ? { planId: plan.id, interval, amount: cents ?? undefined, effectiveFrom: starts, paymentTermsDays: Number(terms) || 0 }
            : undefined,
      },
    }).unwrap()
    onClose()
    navigate(operator.tenant(created.id))
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{adopting ? `Adopt ${adopting}` : 'New tenant'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {Boolean(creation.error) && <Alert severity="error">{problemMessage(creation.error, 'The tenant was not created.')}</Alert>}
          <TextField label="Name" value={name} onChange={(event) => setName(event.target.value)} required autoFocus />
          <TextField
            label="Id"
            value={id}
            onChange={(event) => setId(event.target.value.toLowerCase())}
            required
            disabled={Boolean(adopting)}
            helperText={
              adopting
                ? 'The id its content and administrators are already held under.'
                : 'Lowercase letters, digits and hyphens, like t-natures-way. It is permanent, and never reused.'
            }
          />

          <Divider textAlign="left">Billing</Divider>
          <FormControlLabel
            control={<Checkbox checked={!billed} onChange={(event) => setBilled(!event.target.checked)} />}
            label="Not billed — one of your own"
          />
          {billed && (
            <>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="Billing contact" value={contactName} onChange={(event) => setContactName(event.target.value)} fullWidth />
                <TextField label="Billing email" value={contactEmail} onChange={(event) => setContactEmail(event.target.value)} fullWidth />
              </Stack>
              <TextField select label="Plan" value={planId} onChange={(event) => choosePlan(event.target.value)}>
                <MenuItem value="">
                  <em>Not yet</em>
                </MenuItem>
                {offered.map((candidate) => (
                  <MenuItem key={candidate.id} value={candidate.id}>
                    {candidate.name}
                  </MenuItem>
                ))}
              </TextField>
              {offered.length === 0 && !plans.isLoading && (
                <Typography variant="body2" color="text.secondary">
                  There are no plans yet. Add one under Plans, or register the tenant now and put it on a plan later.
                </Typography>
              )}
              {plan && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    select
                    label="Pays"
                    value={interval}
                    onChange={(event) => {
                      const next = event.target.value as 'monthly' | 'annual'
                      setInterval(next)
                      choosePlan(planId, next)
                    }}
                    sx={{ minWidth: 140 }}
                  >
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="annual">Annually</MenuItem>
                  </TextField>
                  <MoneyField label="Amount per period" value={amount} onChange={setAmount} fullWidth />
                  <TextField
                    label="Starts"
                    type="date"
                    value={starts}
                    onChange={(event) => setStarts(event.target.value)}
                    slotProps={{ inputLabel: { shrink: true } }}
                    fullWidth
                  />
                  <TextField label="Terms (days)" value={terms} onChange={(event) => setTerms(event.target.value)} sx={{ minWidth: 110 }} />
                </Stack>
              )}
            </>
          )}

          <Divider textAlign="left">Access</Divider>
          <TextField
            label="First administrator's email"
            value={administratorEmail}
            onChange={(event) => setAdministratorEmail(event.target.value)}
            helperText="Invited by email if they have no account yet."
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!ready || creation.isLoading} onClick={() => submit().catch(shown)}>
          {adopting ? 'Adopt' : 'Register'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/**
 * The operator API's own description. It was served and linked from
 * nowhere, so the only people who could read it already had it.
 */
function ContractDownload() {
  const [read, { isFetching, error }] = useLazyGetOperatorOpenApiyamlQuery()
  const save = async () => {
    const contract = await read().unwrap()
    download('bluu-cms-operator.openapi.yaml', String(contract), 'application/yaml')
  }
  return (
    <>
      {Boolean(error) && (
        <Alert severity="error" sx={{ py: 0 }}>
          {problemMessage(error, 'The API reference could not be read.')}
        </Alert>
      )}
      <Button disabled={isFetching} onClick={() => void save().catch(shown)}>
        API reference
      </Button>
    </>
  )
}
